import { ToISOTimeOptions } from 'luxon';
import { check, memoize, type } from '@mews-framework/utils';
import { accessible, comparable, localizable, manipulable } from '../componentEnhancers';
import { UTCTimezone } from '../constants';
import { DateTimePart, ISOFormat, LocalizationMethod } from '../enums';
import { toLuxon } from '../utils';
import Time from './time';

const LUXON_TIME_ISO_FORMATS = [ISOFormat.TimeSeconds, ISOFormat.TimeMilliseconds];

const PARTS = [DateTimePart.Hours, DateTimePart.Minutes, DateTimePart.Seconds, DateTimePart.Milliseconds] as const;

const FORMATS = [
    [LocalizationMethod.String, 'Time'],
    [LocalizationMethod.ShortString, 'TimeShort'],
] as const;

interface ToISOTimeParams extends ToISOTimeOptions {
    time: Time;
}

export const toTimeLuxon = memoize((time: Time) => {
    type.check(time, Time);
    return toLuxon(time.value, LUXON_TIME_ISO_FORMATS, UTCTimezone);
});

const TimeService = {
    ...comparable(toTimeLuxon),
    ...manipulable(Time, toTimeLuxon, PARTS),
    ...accessible(toTimeLuxon, PARTS),
    ...localizable(Time, toTimeLuxon, FORMATS),
    parse(timeString: string) {
        check.string(timeString);

        const luxon = toLuxon(timeString, LUXON_TIME_ISO_FORMATS, UTCTimezone);
        return Time(luxon);
    },

    valueOf(time: Time) {
        return toTimeLuxon(time).valueOf();
    },

    toISOTime({ time, ...options }: ToISOTimeParams) {
        return toTimeLuxon(time).toISOTime(options);
    },

    isValidTimeString(timeString: string) {
        try {
            this.parse(timeString);
            return true;
        } catch (error) {
            return false;
        }
    },
};

export default TimeService;
